import { inject, Injectable } from '@angular/core';
import { firstValueFrom, from, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Supabase } from './supabase';
import { ActivityAction, ActivityEntityType, ActivityLog, AddonSubscription, AddonType, AppointmentSession, AppointmentStatus, CalendarToken, Candidate, CandidateStatus, Company, CompanyPlan, Contact, DatabaseTables, HeyflowFlow, JobAdvertisement, JobAdvertisementStatus, JobApplication, JobApplicationStatus, JobSession, JobSessionAdvertisement, JobSessionLog, JobSessionLogWithDetails, PartnerInterest, Partner, RecruitingSettings, TABLE_NAMES, TableName, StripeSubscriptionStatus, User } from '../models/database.types';

@Injectable({
  providedIn: 'root'
})
export class Database {
  private supabase = inject(Supabase);

  // Create a new company
  public getCompanyById(id: string): Observable<Company | null> {
    return this.getById(TABLE_NAMES.COMPANIES, id);
  }

  public createCompany(companyData: Partial<Company>): Observable<Company> {
    return this.supabase.createWithoutSelect(TABLE_NAMES.COMPANIES, companyData);
  }

  public updateCompany(id: string, companyData: Partial<Company>): Observable<Company> {
    return this.update(TABLE_NAMES.COMPANIES, id, companyData);
  }

  public deleteCompany(id: string): Observable<void> {
    return this.delete(TABLE_NAMES.COMPANIES, id);
  }

  public createCompanyWithAdmin(company: Partial<Company>, user: Partial<User>): Observable<string> {
    return this.supabase.createCompanyWithAdminUser({
      company_name: company.name ?? 'DefaultCompanyName',
      company_logo_url: company.logo_url ?? null,
      company_color_scheme: company.color_scheme ?? null,
      company_plan: company.plan ?? CompanyPlan.STARTER,
      stripe_subscription_status: company.stripe_subscription_status ?? StripeSubscriptionStatus.TRIALING,
      trial_days: 30,
      user_first_name: user.first_name,
      user_last_name: user.last_name,
      user_language: user.language ?? 'de',
      user_job_title: user.job_title ?? 'CEO',
    });
  }

  // User
  public getUserById(id: string): Observable<User | null> {
    return this.getById(TABLE_NAMES.USERS, id);
  }

  public getUsersByCompany(companyId: string): Observable<User[]> {
    return this.query(TABLE_NAMES.USERS, (query) => 
      query.eq('company_id', companyId)
    );
  }

  public createUser(userData: Partial<User>): Observable<User> {
    return this.insert(TABLE_NAMES.USERS, userData);
  }

  public updateUser(id: string, userData: Partial<User>): Observable<User> {
    return this.update(TABLE_NAMES.USERS, id, userData);
  }

  public deleteUser(id: string): Observable<void> {
    return from(
      (async () => {
        await this.supabase.invokeFunction('delete-user', { userId: id });
      })()
    );
  }

  // Partner
  public getPartnerById(id: string): Observable<Partner | null> {
    return this.getById(TABLE_NAMES.PARTNERS, id);
  }

  public getPartnersByCompany(companyId: string): Observable<Partner[]> {
    return this.query(TABLE_NAMES.PARTNERS, (query) => 
      query.eq('company_id', companyId)
    );
  }

  public createPartner(partnerData: Partial<Partner>): Observable<Partner> {
    return this.insert(TABLE_NAMES.PARTNERS, partnerData);
  }

  public updatePartner(id: string, partnerData: Partial<Partner>): Observable<Partner> {
    return this.update(TABLE_NAMES.PARTNERS, id, partnerData);
  }

  public deletePartner(id: string): Observable<void> {
    return this.validatePartnerDeletion(id).pipe(
      switchMap(() => this.getPartnerById(id)),
      switchMap(partner => {
        if (!partner) {
          throw new Error('Partner not found');
        }
        
        // Delete logo if exists
        if (partner.logo_url) {
          const parsedUrl = this.parseStoragePublicUrl(partner.logo_url);
          if (parsedUrl) {
            return this.deleteFromStorage(parsedUrl.bucket, parsedUrl.path).pipe(
              switchMap(() => this.delete(TABLE_NAMES.PARTNERS, id)),
              catchError(() => {
                // If logo deletion fails, still delete the partner
                return this.delete(TABLE_NAMES.PARTNERS, id);
              })
            );
          }
        }
        
        // No logo to delete, just delete the partner
        return this.delete(TABLE_NAMES.PARTNERS, id);
      })
    );
  }

  // Contact
  public getContactById(id: string): Observable<Contact | null> {
    return this.getById(TABLE_NAMES.CONTACTS, id);
  }

  public getContactsByCompany(companyId: string): Observable<Contact[]> {
    return this.query(TABLE_NAMES.CONTACTS, (query) => 
      query.eq('company_id', companyId)
    );
  }

  public getContactsByPartner(partnerId: string): Observable<Contact[]> {
    return this.query(TABLE_NAMES.CONTACTS, (query) => 
      query.eq('partner_id', partnerId)
    );
  }

  public createContact(contactData: Partial<Contact>): Observable<Contact> {
    return this.insert(TABLE_NAMES.CONTACTS, contactData);
  }

  public updateContact(id: string, contactData: Partial<Contact>): Observable<Contact> {
    return this.update(TABLE_NAMES.CONTACTS, id, contactData);
  }

  public deleteContact(id: string): Observable<void> {
    return this.validateContactDeletion(id).pipe(
      switchMap(() => this.delete(TABLE_NAMES.CONTACTS, id))
    );
  }

  // Calendar Tokens
  public getCalendarTokensByUser(userId: string): Observable<CalendarToken[]> {
    return this.query(TABLE_NAMES.CALENDAR_TOKENS, (query) =>
      query.eq('user_id', userId)
    );
  }

  public getCalendarTokenById(tokenId: string): Observable<CalendarToken | null> {
    return this.getById(TABLE_NAMES.CALENDAR_TOKENS, tokenId);
  }

  public deleteCalendarToken(tokenId: string): Observable<void> {
    return this.delete(TABLE_NAMES.CALENDAR_TOKENS, tokenId);
  }

  public deleteCalendarTokensByUserId(userId: string): Observable<void> {
    return from(
      (async () => {
        const { error } = await this.supabase.getClient()
          .from(TABLE_NAMES.CALENDAR_TOKENS)
          .delete()
          .eq('user_id', userId);
        if (error) throw error;
      })()
    );
  }

  // Appointment Sessions
  public createAppointmentSession(sessionData: Partial<AppointmentSession>): Observable<AppointmentSession> {
    return this.insert(TABLE_NAMES.APPOINTMENT_SESSIONS, sessionData);
  }

  public getAppointmentSessionById(sessionId: string): Observable<AppointmentSession | null> {
    return this.getById(TABLE_NAMES.APPOINTMENT_SESSIONS, sessionId);
  }

  public getAppointmentSessionsByUser(userId: string): Observable<AppointmentSession[]> {
    return this.query(TABLE_NAMES.APPOINTMENT_SESSIONS, (query) =>
      query
        .eq('organizer_id', userId)
        .order('created_at', { ascending: false })
    );
  }

  public getOpenAndConfirmedAppointmentSessionsByUser(userId: string): Observable<AppointmentSession[]> {
    const now = new Date().toISOString();
    return this.query(TABLE_NAMES.APPOINTMENT_SESSIONS, (query) =>
      query
        .eq('organizer_id', userId)
        .or(`status.eq.${AppointmentStatus.WaitingForProposer},status.eq.${AppointmentStatus.WaitingForSelector},status.eq.${AppointmentStatus.Rejected},and(status.eq.${AppointmentStatus.Confirmed},end_time.gt.${now})`)
        .order('created_at', { ascending: false })
    );
  }

  public getPastConfirmedAppointmentSessionsByUser(
    userId: string,
    page = 0,
    pageSize = 20
  ): Observable<{ data: AppointmentSession[]; count: number }> {
    const now = new Date().toISOString();
    const from = page * pageSize;
    const to = from + pageSize - 1;

    return this.query(
      TABLE_NAMES.APPOINTMENT_SESSIONS,
      (query) =>
        query
          .eq('organizer_id', userId)
          .eq('status', AppointmentStatus.Confirmed)
          .lte('end_time', now)
          .order('start_time', { ascending: false })
          .range(from, to),
      '*',
      true
    );
  }

  public getAppointmentSessionsByCompany(companyId: string): Observable<AppointmentSession[]> {
    return this.query(TABLE_NAMES.APPOINTMENT_SESSIONS, (query) =>
      query.eq('company_id', companyId).order('created_at', { ascending: false })
    );
  }

  public deleteAppointmentSession(sessionId: string): Observable<void> {
    return this.delete(TABLE_NAMES.APPOINTMENT_SESSIONS, sessionId);
  }

  public deleteAppointmentSessionWithNotifications(sessionId: string): Observable<void> {
    return this.supabase.invokeFunction('delete-appointment-session', { sessionId });
  }

  // Mail Token Validation
  public createMailTokenValidation(token: string): Observable<void> {
    return from(
      (async () => {
        const { error } = await this.supabase.getClient()
          .from(TABLE_NAMES.MAIL_TOKEN_VALIDATION)
          .insert({ token });
        if (error) throw error;
      })()
    );
  }

  public deleteMailTokenValidation(token: string): Observable<void> {
    return from(
      (async () => {
        const { error } = await this.supabase.getClient()
          .from(TABLE_NAMES.MAIL_TOKEN_VALIDATION)
          .delete()
          .eq('token', token);
        if (error) throw error;
      })()
    );
  }

  // Activity Log
  public createActivityLog(logData: {
    company_id: string;
    user_id: string | null;
    action: ActivityAction;
    entity_type: ActivityEntityType;
    entity_id: string;
    meta: object;
  }): Observable<void> {
    return from(
      (async () => {
        const { error } = await this.supabase.getClient()
          .from(TABLE_NAMES.ACTIVITY_LOG)
          .insert(logData);
        if (error) throw error;
      })()
    );
  }

  /**
   * Get business-relevant activity logs for normal users (Dashboard)
   * Only returns the 3 main actions: session_started, proposals_submitted, appointment_confirmed
   * @param limit Maximum number of logs to return
   * @returns Observable with filtered activity logs
   */
  public getMyActivityLogs(limit = 20): Observable<ActivityLog[]> {
    return from(
      (async () => {
        const { data: user } = await this.supabase.getClient().auth.getUser();
        if (!user?.user?.id) throw new Error('User not authenticated');
        
        // Step 1: Get all appointment sessions for this user
        const { data: sessions, error: sessionsError } = await this.supabase.getClient()
          .from(TABLE_NAMES.APPOINTMENT_SESSIONS)
          .select('id')
          .eq('organizer_id', user.user.id);
        
        if (sessionsError) throw sessionsError;
        if (!sessions || sessions.length === 0) return [];
        
        // Step 2: Get activity logs for these sessions (filtered to business-relevant actions only)
        const sessionIds = sessions.map(s => s.id);
        const { data, error } = await this.supabase.getClient()
          .from(TABLE_NAMES.ACTIVITY_LOG)
          .select('*')
          .in('entity_id', sessionIds)
          .in('action', [
            ActivityAction.SessionStarted,
            ActivityAction.ProposalsSubmitted,
            ActivityAction.ProposalsReplaced,
            ActivityAction.AppointmentConfirmed,
            ActivityAction.AppointmentRejected
          ])
          .order('created_at', { ascending: false })
          .limit(limit);
        
        if (error) throw error;
        return (data || []) as ActivityLog[];
      })()
    );
  }

  /**
   * Get complete audit logs for admin users (Audit-Log Dashboard)
   * Returns all activity actions including system events
   * Requires admin or super_admin role (enforced by RLS)
   * @param companyId Company ID to filter logs
   * @param startDate Optional start date for filtering (ISO string)
   * @param endDate Optional end date for filtering (ISO string)
   * @param limit Maximum number of logs to return
   * @returns Observable with all activity logs
   */
  public getCompanyAuditLogs(companyId: string, startDate?: string, endDate?: string, limit = 10000): Observable<ActivityLog[]> {
    return from(
      (async () => {
        let query = this.supabase.getClient()
          .from(TABLE_NAMES.ACTIVITY_LOG)
          .select('*')
          .eq('company_id', companyId);

        if (startDate) {
          query = query.gte('created_at', startDate);
        }
        if (endDate) {
          query = query.lte('created_at', endDate);
        }

        const { data, error } = await query
          .order('created_at', { ascending: false })
          .limit(limit);
        
        if (error) throw error;
        return (data || []) as ActivityLog[];
      })()
    );
  }

  // ============================================
  // ADDON SUBSCRIPTIONS
  // ============================================

  public getAddonSubscriptionsByCompany(companyId: string): Observable<AddonSubscription[]> {
    return from(
      this.supabase.getClient()
        .from(TABLE_NAMES.ADDON_SUBSCRIPTIONS)
        .select('*')
        .eq('company_id', companyId)
    ).pipe(
      switchMap(({ data, error }) => {
        if (error) return throwError(() => error);
        return of((data || []) as AddonSubscription[]);
      })
    );
  }

  public getAddonSubscription(companyId: string, addonType: AddonType): Observable<AddonSubscription | null> {
    return from(
      this.supabase.getClient()
        .from(TABLE_NAMES.ADDON_SUBSCRIPTIONS)
        .select('*')
        .eq('company_id', companyId)
        .eq('addon_type', addonType)
        .maybeSingle()
    ).pipe(
      switchMap(({ data, error }) => {
        if (error) return throwError(() => error);
        return of(data as AddonSubscription | null);
      })
    );
  }

  public createAddonSubscription(subscription: Partial<AddonSubscription>): Observable<AddonSubscription> {
    return this.supabase.createWithoutSelect(TABLE_NAMES.ADDON_SUBSCRIPTIONS, subscription);
  }

  public updateAddonSubscription(id: string, updates: Partial<AddonSubscription>): Observable<AddonSubscription> {
    return this.update(TABLE_NAMES.ADDON_SUBSCRIPTIONS, id, updates);
  }

  public deleteAddonSubscription(id: string): Observable<void> {
    return this.delete(TABLE_NAMES.ADDON_SUBSCRIPTIONS, id);
  }

  // ============================================
  // JOB ADVERTISEMENTS
  // ============================================

  public getActiveJobAdvertisementsByCompany(companyId: string): Observable<JobAdvertisement[]> {
    return from(
      this.supabase.getClient()
        .from(TABLE_NAMES.JOB_ADVERTISEMENTS)
        .select('*')
        .eq('company_id', companyId)
        .eq('status', JobAdvertisementStatus.Active)
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          if (error) throw error;
          return data as JobAdvertisement[];
        })
    );
  }

  public getJobAdvertisementsByCompanyPaginated(
    companyId: string,
    page = 0,
    pageSize = 20
  ): Observable<{ data: JobAdvertisement[]; count: number }> {
    const from = page * pageSize;
    const to = from + pageSize - 1;

    return this.query(
      TABLE_NAMES.JOB_ADVERTISEMENTS,
      (query) =>
        query
          .eq('company_id', companyId)
          .order('created_at', { ascending: false })
          .range(from, to),
      '*',
      true
    );
  }

  public getJobAdvertisementsByPartner(partnerId: string): Observable<JobAdvertisement[]> {
    return from(
      this.supabase.getClient()
        .from(TABLE_NAMES.JOB_ADVERTISEMENTS)
        .select('*')
        .eq('partner_id', partnerId)
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          if (error) throw error;
          return data as JobAdvertisement[];
        })
    );
  }

  public getJobAdvertisementById(id: string): Observable<JobAdvertisement | null> {
    return this.getById(TABLE_NAMES.JOB_ADVERTISEMENTS, id);
  }

  public getActiveJobAdvertisements(companyId: string): Observable<JobAdvertisement[]> {
    return from(
      this.supabase.getClient()
        .from(TABLE_NAMES.JOB_ADVERTISEMENTS)
        .select('*')
        .eq('company_id', companyId)
        .eq('status', JobAdvertisementStatus.Active)
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          if (error) throw error;
          return data as JobAdvertisement[];
        })
    );
  }

  public createJobAdvertisement(jobAd: Partial<JobAdvertisement>): Observable<JobAdvertisement> {
    return this.supabase.createWithoutSelect(TABLE_NAMES.JOB_ADVERTISEMENTS, jobAd);
  }

  public updateJobAdvertisement(id: string, updates: Partial<JobAdvertisement>): Observable<JobAdvertisement> {
    return this.update(TABLE_NAMES.JOB_ADVERTISEMENTS, id, updates);
  }

  public deleteJobAdvertisement(id: string): Observable<void> {
    return this.delete(TABLE_NAMES.JOB_ADVERTISEMENTS, id);
  }

  // ============================================
  // CANDIDATES
  // ============================================

  public getCandidatesByCompany(companyId: string): Observable<Candidate[]> {
    return from(
      this.supabase.getClient()
        .from(TABLE_NAMES.CANDIDATES)
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          if (error) throw error;
          return data as Candidate[];
        })
    );
  }

  public getCandidatesByCompanyPaginated(
    companyId: string,
    page = 0,
    pageSize = 20
  ): Observable<{ data: Candidate[]; count: number }> {
    const from = page * pageSize;
    const to = from + pageSize - 1;

    return this.query(
      TABLE_NAMES.CANDIDATES,
      (query) =>
        query
          .eq('company_id', companyId)
          .order('created_at', { ascending: false })
          .range(from, to),
      '*',
      true
    );
  }

  public getCandidateById(id: string): Observable<Candidate | null> {
    return this.getById(TABLE_NAMES.CANDIDATES, id);
  }

  public getCandidatesByStatus(companyId: string, status: CandidateStatus): Observable<Candidate[]> {
    return from(
      this.supabase.getClient()
        .from(TABLE_NAMES.CANDIDATES)
        .select('*')
        .eq('company_id', companyId)
        .eq('status', status)
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          if (error) throw error;
          return data as Candidate[];
        })
    );
  }

  public getCandidateByEmail(email: string): Observable<Candidate | null> {
    return from(
      this.supabase.getClient()
        .from(TABLE_NAMES.CANDIDATES)
        .select('*')
        .eq('email', email)
        .maybeSingle()
        .then(({ data, error }) => {
          if (error) throw error;
          return data as Candidate | null;
        })
    );
  }

  public createCandidate(candidate: Partial<Candidate>): Observable<Candidate> {
    const client = this.supabase.getClient();
    return from(
      (async () => {
        const { data, error } = await client
          .from(TABLE_NAMES.CANDIDATES)
          .insert(candidate)
          .select()
          .single();
        if (error) throw error;
        return data as Candidate;
      })()
    );
  }

  public updateCandidate(id: string, updates: Partial<Candidate>): Observable<Candidate> {
    return this.update(TABLE_NAMES.CANDIDATES, id, updates);
  }

  public deleteCandidate(id: string): Observable<void> {
    return this.delete(TABLE_NAMES.CANDIDATES, id);
  }

  // ============================================
  // JOB SESSIONS
  // ============================================

  public getJobSessionsByRecruiter(recruiterId: string): Observable<JobSession[]> {
    return from(
      this.supabase.getClient()
        .from(TABLE_NAMES.JOB_SESSIONS)
        .select('*')
        .eq('recruiter_id', recruiterId)
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          if (error) throw error;
          return data as JobSession[];
        })
    );
  }

  public getJobSessionsByRecruiterPaginated(
    recruiterId: string,
    page = 0,
    pageSize = 10
  ): Observable<{ data: JobSession[]; count: number }> {
    const from = page * pageSize;
    const to = from + pageSize - 1;

    return this.query(
      TABLE_NAMES.JOB_SESSIONS,
      (query) =>
        query
          .eq('recruiter_id', recruiterId)
          .order('created_at', { ascending: false })
          .range(from, to),
      '*',
      true
    );
  }

  public getJobSessionsByCandidate(candidateId: string): Observable<JobSession[]> {
    return from(
      this.supabase.getClient()
        .from(TABLE_NAMES.JOB_SESSIONS)
        .select('*')
        .eq('candidate_id', candidateId)
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          if (error) throw error;
          return data as JobSession[];
        })
    );
  }

  public getJobSessionById(id: string): Observable<JobSession | null> {
    return this.getById(TABLE_NAMES.JOB_SESSIONS, id);
  }

  public getJobSessionByToken(token: string): Observable<JobSession | null> {
    return from(
      this.supabase.getClient()
        .from(TABLE_NAMES.JOB_SESSIONS)
        .select('*')
        .eq('candidate_token', token)
        .eq('shared_to_candidate', true)
        .single()
        .then(({ data, error }) => {
          if (error) return null;
          return data as JobSession;
        })
    );
  }

  public createJobSession(session: Partial<JobSession>): Observable<JobSession> {
    const client = this.supabase.getClient();
    return from(
      (async () => {
        const { data, error } = await client
          .from(TABLE_NAMES.JOB_SESSIONS)
          .insert(session)
          .select()
          .single();
        if (error) throw error;
        return data as JobSession;
      })()
    );
  }

  public updateJobSession(id: string, updates: Partial<JobSession>): Observable<JobSession> {
    return this.update(TABLE_NAMES.JOB_SESSIONS, id, updates);
  }

  public deleteJobSession(id: string): Observable<void> {
    return this.delete(TABLE_NAMES.JOB_SESSIONS, id);
  }

  // ============================================
  // JOB SESSION ADVERTISEMENTS
  // ============================================

  public getJobSessionAdvertisements(sessionId: string): Observable<JobSessionAdvertisement[]> {
    return from(
      this.supabase.getClient()
        .from(TABLE_NAMES.JOB_SESSION_ADVERTISEMENTS)
        .select('*')
        .eq('job_session_id', sessionId)
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          if (error) throw error;
          return data as JobSessionAdvertisement[];
        })
    );
  }

  public getJobSessionAdvertisementsByPartnerToken(partnerToken: string): Observable<JobSessionAdvertisement[]> {
    return from(
      this.supabase.getClient()
        .from(TABLE_NAMES.JOB_SESSION_ADVERTISEMENTS)
        .select('*')
        .eq('partner_token', partnerToken)
        .eq('shared_to_partner', true)
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          if (error) throw error;
          return data as JobSessionAdvertisement[];
        })
    );
  }

  public addJobAdvertisementToSession(
    data: Partial<JobSessionAdvertisement>
  ): Observable<JobSessionAdvertisement> {
    const client = this.supabase.getClient();
    return from(
      (async () => {
        const insertData = {
          ...data,
          partner_interest: data.partner_interest ?? PartnerInterest.Pending,
          shared_to_partner: data.shared_to_partner ?? false
        };
        const { data: result, error } = await client
          .from(TABLE_NAMES.JOB_SESSION_ADVERTISEMENTS)
          .insert(insertData)
          .select()
          .single();
        if (error) throw error;
        return result as JobSessionAdvertisement;
      })()
    );
  }

  public updateJobSessionAdvertisement(
    id: string,
    updates: Partial<JobSessionAdvertisement>
  ): Observable<JobSessionAdvertisement> {
    return this.update(TABLE_NAMES.JOB_SESSION_ADVERTISEMENTS, id, updates);
  }

  public removeJobAdvertisementFromSession(id: string): Observable<void> {
    return this.delete(TABLE_NAMES.JOB_SESSION_ADVERTISEMENTS, id);
  }

  // ============================================
  // JOB SESSION LOGS
  // ============================================

  public getJobSessionLogs(sessionId: string, limit = 50): Observable<JobSessionLog[]> {
    return from(
      this.supabase.getClient()
        .from(TABLE_NAMES.JOB_SESSION_LOGS)
        .select('*')
        .eq('job_session_id', sessionId)
        .order('created_at', { ascending: false })
        .limit(limit)
        .then(({ data, error }) => {
          if (error) throw error;
          return data as JobSessionLog[];
        })
    );
  }

  public getJobSessionLogsWithDetails(sessionId: string, limit = 50): Observable<JobSessionLogWithDetails[]> {
    return from(
      this.supabase.getClient()
        .from(TABLE_NAMES.JOB_SESSION_LOGS)
        .select(`
          *,
          job_sessions!inner(candidate_id, candidates(first_name, last_name))
        `)
        .eq('job_session_id', sessionId)
        .order('created_at', { ascending: false })
        .limit(limit)
        .then(async ({ data, error }) => {
          if (error) throw error;
          
          // Enrich logs with job and partner data from metadata
          const enrichedLogs = await Promise.all((data || []).map(async (log: any) => {
            const enriched: any = {
              ...log,
              candidate_first_name: log.job_sessions?.candidates?.first_name,
              candidate_last_name: log.job_sessions?.candidates?.last_name
            };
            
            // Load job title if job_advertisement_id is in metadata
            if (log.metadata?.job_advertisement_id) {
              const { data: jobAd } = await this.supabase.getClient()
                .from(TABLE_NAMES.JOB_ADVERTISEMENTS)
                .select('title')
                .eq('id', log.metadata.job_advertisement_id)
                .single();
              if (jobAd) enriched.job_title = jobAd.title;
            }
            
            // Load partner name if partner_id is in metadata
            if (log.metadata?.partner_id) {
              const { data: partner } = await this.supabase.getClient()
                .from(TABLE_NAMES.PARTNERS)
                .select('name')
                .eq('id', log.metadata.partner_id)
                .single();
              if (partner) enriched.partner_name = partner.name;
            }
            
            return enriched;
          }));
          
          return enrichedLogs as JobSessionLogWithDetails[];
        })
    );
  }

  public getRecentJobSessionLogsByRecruiter(recruiterId: string, limit = 50): Observable<JobSessionLogWithDetails[]> {
    return from(
      this.supabase.getClient()
        .from(TABLE_NAMES.JOB_SESSION_LOGS)
        .select(`
          *,
          job_sessions!inner(recruiter_id, name, candidate_id, candidates(first_name, last_name))
        `)
        .eq('job_sessions.recruiter_id', recruiterId)
        .order('created_at', { ascending: false })
        .limit(limit)
        .then(async ({ data, error }) => {
          if (error) throw error;
          
          // Enrich logs with job and partner data from metadata
          const enrichedLogs = await Promise.all((data || []).map(async (log: any) => {
            const enriched: any = {
              ...log,
              candidate_first_name: log.job_sessions?.candidates?.first_name,
              candidate_last_name: log.job_sessions?.candidates?.last_name
            };
            
            // Load job title if job_advertisement_id is in metadata
            if (log.metadata?.job_advertisement_id) {
              const { data: jobAd } = await this.supabase.getClient()
                .from(TABLE_NAMES.JOB_ADVERTISEMENTS)
                .select('title')
                .eq('id', log.metadata.job_advertisement_id)
                .single();
              if (jobAd) enriched.job_title = jobAd.title;
            }
            
            // Load partner name if partner_id is in metadata
            if (log.metadata?.partner_id) {
              const { data: partner } = await this.supabase.getClient()
                .from(TABLE_NAMES.PARTNERS)
                .select('name')
                .eq('id', log.metadata.partner_id)
                .single();
              if (partner) enriched.partner_name = partner.name;
            }
            
            return enriched;
          }));
          
          return enrichedLogs as JobSessionLogWithDetails[];
        })
    );
  }

  public createJobSessionLog(log: Partial<JobSessionLog>): Observable<JobSessionLog> {
    const client = this.supabase.getClient();
    return from(
      (async () => {
        const { data, error } = await client
          .from(TABLE_NAMES.JOB_SESSION_LOGS)
          .insert(log)
          .select()
          .single();
        if (error) throw error;
        return data as JobSessionLog;
      })()
    );
  }

  // Activity Log Anonymization (GDPR/DSG Compliance)
  
  /**
   * Anonymizes activity logs for a specific company plan according to retention policy
   * @param plan Company plan ('business' or 'enterprise')
   * @param monthsOld Number of months after which to anonymize (12 for business, 24 for enterprise)
   * @returns Observable with count of anonymized logs
   */
  public anonymizeActivityLogsByPlan(plan: 'business' | 'enterprise', monthsOld: number): Observable<number> {
    return from(
      (async () => {
        const { data, error } = await this.supabase.getClient()
          .rpc('anonymize_activity_logs_by_plan', {
            plan_name: plan,
            months_old: monthsOld
          });
        
        if (error) throw error;
        return data?.[0]?.anonymized_count || 0;
      })()
    );
  }

  /**
   * Anonymizes activity logs for all plans according to default retention policy
   * Business: 12 months, Enterprise: 24 months
   * @returns Observable with anonymization results per plan
   */
  public anonymizeActivityLogsAllPlans(): Observable<{ plan: string; anonymized_count: number }[]> {
    return from(
      (async () => {
        const { data, error } = await this.supabase.getClient()
          .rpc('anonymize_activity_logs_all_plans');
        
        if (error) throw error;
        return data || [];
      })()
    );
  }

  /**
   * Deletes activity logs older than 10 years (compliance requirement)
   * @returns Observable with count of deleted logs
   */
  public deleteOldActivityLogs(): Observable<number> {
    return from(
      (async () => {
        const { data, error } = await this.supabase.getClient()
          .rpc('delete_old_activity_logs');
        
        if (error) throw error;
        return data || 0;
      })()
    );
  }

  // Storage
  public uploadToStorage(bucket: string, path: string, file: File ): Observable<string> {
    const client = this.supabase.getClient();
    return from(
      (async () => {
        const { error } = await client.storage
          .from(bucket)
          .upload(path, file);
        if (error) throw error;
        return client.storage.from(bucket).getPublicUrl(path).data.publicUrl;
      })()
    );
  }

  /**
 * Delete a file from Supabase Storage by bucket and path
 */
  public deleteFromStorage(bucket: string, path: string): Observable<void> {
    const client = this.supabase.getClient();
    return from(
      (async () => {
        const { error } = await client.storage.from(bucket).remove([path]);
        if (error) throw error;
      })()
    );
  }

  /**
 * Parses a storage public URL into bucket and path components
 * @param url The full public URL of the stored file
 * @returns Object containing bucket and path, or null if URL is invalid
 */
  public parseStoragePublicUrl(url: string): { bucket: string; path: string } | null {
    try {
      const marker = '/storage/v1/object/public/';
      const i = url.indexOf(marker);
      if (i === -1) return null;
      const rest = url.substring(i + marker.length);
      const slash = rest.indexOf('/');
      if (slash === -1) return null;
      const bucket = rest.substring(0, slash);
      const path = rest.substring(slash + 1);
      return { bucket, path };
    } catch {
      return null;
    }
  }

  getSignedUrlFromStorageUrl(fileUrl: string, expiresIn = 3600): Observable<string> {
    // If it's already a signed URL or external URL, return as is
    if (fileUrl.includes('token=') || !fileUrl.includes('/storage/v1/object/')) {
      return of(fileUrl);
    }

    // Parse the storage URL to get bucket and path
    const parsed = this.parseStoragePublicUrl(fileUrl);
    if (!parsed) {
      return of(fileUrl); // Return original if parsing fails
    }

    return this.getSignedUrl(parsed.bucket, parsed.path, expiresIn);
  }

  getSignedUrl(bucket: string, path: string, expiresIn = 3600): Observable<string> {
    const client = this.supabase.getClient();
    return from(
      (async () => {
        const { data, error } = await client.storage
          .from(bucket)
          .createSignedUrl(path, expiresIn);
        if (error) throw error;
        return data.signedUrl;
      })()
    );
  }

  // ============================================
  // RECRUITING SETTINGS
  // ============================================

  public getRecruitingSettings(userId: string): Observable<RecruitingSettings | null> {
    return from(
      this.supabase.getClient()
        .from(TABLE_NAMES.RECRUITING_SETTINGS)
        .select('*')
        .eq('user_id', userId)
        .maybeSingle()
        .then(({ data, error }) => {
          if (error) return null;
          return data as RecruitingSettings | null;
        })
    );
  }

  public createRecruitingSettings(userId: string, settings: Partial<RecruitingSettings>): Observable<RecruitingSettings> {
    return from(
      (async () => {
        const { data, error } = await this.supabase.getClient()
          .from(TABLE_NAMES.RECRUITING_SETTINGS)
          .insert({ user_id: userId, ...settings })
          .select()
          .single();
        if (error) throw error;
        return data as RecruitingSettings;
      })()
    );
  }

  public updateRecruitingSettingsById(id: string, settings: Partial<RecruitingSettings>): Observable<RecruitingSettings> {
    return from(
      (async () => {
        const { data, error } = await this.supabase.getClient()
          .from(TABLE_NAMES.RECRUITING_SETTINGS)
          .update({ ...settings, updated_at: new Date().toISOString() })
          .eq('id', id)
          .select()
          .single();
        if (error) throw error;
        return data as RecruitingSettings;
      })()
    );
  }

  public updateRecruitingSettings(userId: string, settings: Partial<RecruitingSettings>): Observable<RecruitingSettings> {
    return from(
      (async () => {
        const { data, error } = await this.supabase.getClient()
          .from(TABLE_NAMES.RECRUITING_SETTINGS)
          .upsert({
            user_id: userId,
            ...settings,
            updated_at: new Date().toISOString()
          })
          .select()
          .single();
        if (error) throw error;
        return data as RecruitingSettings;
      })()
    );
  }

  public addHeyflowFlow(userId: string, flow: HeyflowFlow): Observable<RecruitingSettings> {
    return this.getRecruitingSettings(userId).pipe(
      switchMap(settings => {
        const currentFlows = settings?.heyflow_flows || [];
        const updatedFlows = [...currentFlows, flow];
        
        if (settings) {
          return this.updateRecruitingSettingsById(settings.id, { heyflow_flows: updatedFlows });
        } else {
          return this.createRecruitingSettings(userId, { heyflow_flows: updatedFlows });
        }
      })
    );
  }

  public removeHeyflowFlow(userId: string, flowId: string): Observable<RecruitingSettings> {
    return this.getRecruitingSettings(userId).pipe(
      switchMap(settings => {
        if (!settings) {
          throw new Error('No settings found for user');
        }

        const currentFlows = settings.heyflow_flows || [];
        const updatedFlows = currentFlows.filter((f: HeyflowFlow) => f.id !== flowId);
        return this.updateRecruitingSettingsById(settings.id, { heyflow_flows: updatedFlows });
      })
    );
  }

  // JobApplication Methods

  public createJobApplication(application: Omit<JobApplication, 'id' | 'created_at' | 'updated_at'>): Observable<JobApplication> {
    return this.supabase.createWithoutSelect<JobApplication>(TABLE_NAMES.JOB_APPLICATIONS, application);
  }

  public getJobApplicationsByJobId(jobId: string): Observable<JobApplication[]> {
    return this.query(TABLE_NAMES.JOB_APPLICATIONS, (query: any) => {
      return query.eq('job_advertisement_id', jobId);
    });
  }

  public getJobApplicationsByCandidateId(candidateId: string): Observable<JobApplication[]> {
    return this.query(TABLE_NAMES.JOB_APPLICATIONS, (query: any) => {
      return query.eq('candidate_id', candidateId);
    });
  }

  public getPendingJobApplications(companyId: string): Observable<JobApplication[]> {
    return this.query(TABLE_NAMES.JOB_APPLICATIONS, (query: any) => {
      return query
        .eq('status', JobApplicationStatus.PendingReview)
        .eq('company_id', companyId)
        .select(`
          *,
          candidate:candidate_id (first_name, last_name),
          job_advertisement:job_advertisement_id (title)
        `);
    });
  }

  public updateJobApplicationStatus(id: string, status: JobApplicationStatus): Observable<JobApplication> {
    return this.update(TABLE_NAMES.JOB_APPLICATIONS, id, { status });
  }

  /**
   * Advanced query with custom filters for a specific table (without count)
   */
  query<T extends TableName>(
    tableName: T,
    queryBuilder: (query: any) => any,
    select?: string
  ): Observable<DatabaseTables[T][]>;

  /**
   * Advanced query with custom filters for a specific table (with count)
   */
  query<T extends TableName>(
    tableName: T,
    queryBuilder: (query: any) => any,
    select: string,
    withCount: true
  ): Observable<{ data: DatabaseTables[T][]; count: number }>;

  /**
   * Implementation
   */
  query<T extends TableName>(
    tableName: T,
    queryBuilder: (query: any) => any,
    select?: string,
    withCount?: boolean
  ): Observable<DatabaseTables[T][] | { data: DatabaseTables[T][]; count: number }> {
    if (withCount) {
      return this.supabase.query<DatabaseTables[T]>(tableName, queryBuilder, select ?? '*', true);
    }
    return this.supabase.query<DatabaseTables[T]>(tableName, queryBuilder, select);
  }  

  /**
  * Get a single record by ID from a specific table
  */
  private getById<T extends TableName>(
      tableName: T, 
      id: string | number, 
      select?: string
    ): Observable<DatabaseTables[T] | null> {
      return this.supabase.getById<DatabaseTables[T]>(tableName, id, select);
  }

  /**
 * Insert a new record into a specific table
 */
  private insert<T extends TableName>(
    tableName: T, 
    data: Partial<DatabaseTables[T]>
  ): Observable<DatabaseTables[T]> {
    return this.supabase.insert<DatabaseTables[T]>(tableName, data);
  }

  /**
   * Update a record by ID in a specific table
   */
  private update<T extends TableName>(
    tableName: T, 
    id: string | number, 
    data: Partial<DatabaseTables[T]>
  ): Observable<DatabaseTables[T]> {
    return this.supabase.update<DatabaseTables[T]>(tableName, id, data);
  }

  /**
   * Delete a record by ID from a specific table
   */
  private delete<T extends TableName>(tableName: T, id: string | number): Observable<void> {
    return this.supabase.delete(tableName, id);
  }

  /**
   * Validates if a contact can be deleted by checking for active appointment sessions
   * @param contactId The ID of the contact to validate
   * @returns Observable that completes if valid, or throws error if contact has active sessions
   */
  private validateContactDeletion(contactId: string): Observable<void> {
    return from(
      (async () => {
        const now = new Date().toISOString();
        
        // Check for sessions in waiting states
        const { data: waitingSessions, error: waitingError } = await this.supabase.getClient()
          .from(TABLE_NAMES.APPOINTMENT_SESSIONS)
          .select('id, status')
          .eq('proposer_contact_id', contactId)
          .in('status', [
            AppointmentStatus.WaitingForProposer,
            AppointmentStatus.WaitingForSelector
          ]);
        
        if (waitingError) throw waitingError;
        
        if (waitingSessions && waitingSessions.length > 0) {
          const error: any = new Error('contact.delete.validation.waitingSessions');
          error.translationKey = 'contact.delete.validation.waitingSessions';
          error.translationParams = { count: waitingSessions.length };
          throw error;
        }
        
        // Check for confirmed sessions with future end_time
        const { data: confirmedSessions, error: confirmedError } = await this.supabase.getClient()
          .from(TABLE_NAMES.APPOINTMENT_SESSIONS)
          .select('id, end_time')
          .eq('contact_id', contactId)
          .eq('status', AppointmentStatus.Confirmed)
          .gt('end_time', now);
        
        if (confirmedError) throw confirmedError;
        
        if (confirmedSessions && confirmedSessions.length > 0) {
          const error: any = new Error('contact.delete.validation.confirmedSessions');
          error.translationKey = 'contact.delete.validation.confirmedSessions';
          error.translationParams = { count: confirmedSessions.length };
          throw error;
        }
        
        // Delete past confirmed sessions (no validation needed for these)
        const { data: pastSessions, error: pastSessionsError } = await this.supabase.getClient()
          .from(TABLE_NAMES.APPOINTMENT_SESSIONS)
          .select('id')
          .eq('contact_id', contactId)
          .eq('status', AppointmentStatus.Confirmed)
          .lte('end_time', now);
        
        if (pastSessionsError) throw pastSessionsError;
        
        // Delete each past session using the edge function (without sending emails)
        if (pastSessions && pastSessions.length > 0) {
          for (const session of pastSessions) {
            await firstValueFrom(this.deleteAppointmentSessionWithNotifications(session.id));
          }
        }
      })()
    );
  }

  /**
   * Validates if a partner can be deleted by checking all associated contacts
   * @param partnerId The ID of the partner to validate
   * @returns Observable that completes if valid, or throws error if any contact has active sessions
   */
  private validatePartnerDeletion(partnerId: string): Observable<void> {
    return this.getContactsByPartner(partnerId).pipe(
      switchMap(contacts => {
        if (contacts.length === 0) {
          return of(void 0);
        }
        
        // Validate all contacts
        const validations = contacts.map(contact => 
          this.validateContactDeletion(contact.id).pipe(
            catchError(error => {
              const partnerError: any = new Error('partner.delete.validation.contactHasActiveSessions');
              partnerError.translationKey = 'partner.delete.validation.contactHasActiveSessions';
              partnerError.translationParams = { 
                firstName: contact.first_name, 
                lastName: contact.last_name,
                details: error.translationKey ? '' : error.message
              };
              return throwError(() => partnerError);
            })
          )
        );
        
        return from(
          (async () => {
            for (const validation of validations) {
              await validation.toPromise();
            }
          })()
        );
      })
    );
  }
}
